import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Search, Circle, CheckCircle2, X, Download, ArrowDownUp, Check, Plus, Trash2 } from 'lucide-react';
import { supabase, User, Category } from '../lib/supabase';
import './TransactionsScreen.css';

interface TransactionsScreenProps {
  userId: string;
  currentUser: User;
}

type TxType = 'daily' | 'recurring' | 'large';
const ALL_TYPES: TxType[] = ['daily', 'recurring', 'large'];

interface TransactionRow {
  id: string;
  type: TxType;
  amount: number;
  note?: string;
  verified: boolean;
  created_at: string;
  category_name: string;
  category_color: string;
  user_name: string;
  user_color: string;
  monthlyAmount?: number;
  totalCharged?: number;
  fallOffDate?: string;
}

const currentYear = new Date().getFullYear();

const TransactionsScreen: React.FC<TransactionsScreenProps> = ({ userId, currentUser }) => {
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYears, setSelectedYears] = useState<Set<number>>(new Set([currentYear]));
  const [yearRangeStart, setYearRangeStart] = useState(currentYear - 2);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<TxType>>(new Set(ALL_TYPES));
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addNote, setAddNote] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [addDate, setAddDate] = useState(new Date().toISOString().slice(0, 10));
  const [addUserId, setAddUserId] = useState(userId);
  const [addCategoryId, setAddCategoryId] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const defaultCatSet = useRef(false);

  const visibleYears = [yearRangeStart, yearRangeStart + 1, yearRangeStart + 2, yearRangeStart + 3];

  const toggleYear = (year: number) => {
    setSelectedYears(prev => {
      const next = new Set(prev);
      if (next.has(year)) {
        if (next.size > 1) next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const toggleFilter = (filter: TxType) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
  };

  const allFiltersActive = activeFilters.size === ALL_TYPES.length;
  const noFiltersActive = activeFilters.size === 0;
  const showAll = allFiltersActive || noFiltersActive;

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const years = Array.from(selectedYears).sort();
      const minYear = years[0];
      const maxYear = years[years.length - 1];
      const startDate = new Date(minYear, 0, 1).toISOString();
      const endDate = new Date(maxYear, 11, 31, 23, 59, 59, 999).toISOString();

      const [
        { data: expensesData, error: expensesError },
        { data: usersData, error: usersError },
        { data: categoriesData, error: categoriesError },
        { data: recurringData, error: recurringError },
        { data: largeData, error: largeError },
      ] = await Promise.all([
        supabase
          .from('expenses')
          .select('*')
          .gte('created_at', startDate)
          .lte('created_at', endDate)
          .order('created_at', { ascending: false }),
        supabase.from('users').select('*'),
        supabase.from('categories').select('*'),
        supabase.from('recurring_expenses').select('*'),
        supabase.from('large_expenses').select('*'),
      ]);

      if (expensesError) throw expensesError;
      if (usersError) throw usersError;
      if (categoriesError) throw categoriesError;
      if (recurringError) throw recurringError;
      if (largeError) throw largeError;

      const usersList = (usersData || []) as User[];
      const categoriesList = (categoriesData || []) as Category[];
      setAllUsers(usersList);
      setAllCategories(categoriesList);
      if (!defaultCatSet.current && categoriesList.length > 0) {
        const normalCats = categoriesList.filter(c => !c.is_recurring_only && !c.is_large_expense_only);
        if (normalCats.length > 0) {
          setAddCategoryId(normalCats[0].id);
          defaultCatSet.current = true;
        }
      }

      const usersMap = new Map(usersList.map(u => [u.id, u]));
      const categoriesMap = new Map(categoriesList.map(c => [c.id, c]));

      const selectedYearsSet = new Set(years);
      const now = new Date();
      const dataFloor = new Date(2025, 8, 1); // Sept 1, 2025

      const dailyRows: TransactionRow[] = (expensesData || []).filter(e => {
        const d = new Date(e.created_at);
        if (d < dataFloor) return false;
        if (d > now) return false;
        if (!selectedYearsSet.has(d.getFullYear())) return false;
        return true;
      }).map(e => {
        const cat = categoriesMap.get(e.category_id);
        const user = usersMap.get(e.user_id);
        return {
          id: e.id,
          type: 'daily' as TxType,
          amount: e.amount,
          note: e.note,
          verified: e.verified ?? false,
          created_at: e.created_at,
          category_name: cat?.name ?? 'Unknown',
          category_color: cat?.color ?? '#666',
          user_name: user?.name ?? 'Unknown',
          user_color: user?.color ?? '#666',
        };
      });

      const recurringRows: TransactionRow[] = (recurringData || []).map(r => {
        const cat = categoriesMap.get(r.category_id);
        const user = usersMap.get(r.user_id);
        const createdAt = new Date(r.created_at);

        let totalMonths = 0;
        for (const yr of years) {
          const yrStart = new Date(yr, 0, 1);
          const yrEnd = new Date(yr, 11, 31);
          const startMonth = createdAt > yrStart ? createdAt.getMonth() : 0;
          let endMonth: number;
          if (r.is_active) {
            endMonth = now.getFullYear() === yr ? now.getMonth() : (now > yrEnd ? 11 : -1);
          } else {
            const updatedAt = new Date(r.updated_at);
            endMonth = updatedAt.getFullYear() === yr ? updatedAt.getMonth() : (updatedAt > yrEnd ? 11 : -1);
          }
          if (createdAt.getFullYear() > yr) endMonth = -1;
          totalMonths += Math.max(0, endMonth - startMonth + 1);
        }

        const totalCharged = totalMonths * r.amount;

        return {
          id: `recurring-${r.id}`,
          type: 'recurring' as TxType,
          amount: r.amount,
          monthlyAmount: r.amount,
          totalCharged,
          note: r.note,
          verified: false,
          created_at: r.created_at,
          category_name: cat?.name ?? 'Unknown',
          category_color: cat?.color ?? '#666',
          user_name: user?.name ?? 'Unknown',
          user_color: user?.color ?? '#666',
        };
      }).filter(r => r.totalCharged! > 0);

      const largeRows: TransactionRow[] = (largeData || []).map(l => {
        const cat = categoriesMap.get(l.category_id);
        const user = usersMap.get(l.user_id);

        const createdAt = new Date(l.created_at);
        const fallOff = new Date(createdAt);
        fallOff.setMonth(fallOff.getMonth() + 12);

        return {
          id: `large-${l.id}`,
          type: 'large' as TxType,
          amount: l.monthly_amount,
          monthlyAmount: l.monthly_amount,
          totalCharged: l.total_amount,
          note: l.note,
          verified: false,
          created_at: l.created_at,
          fallOffDate: fallOff.toISOString(),
          category_name: cat?.name ?? 'Unknown',
          category_color: cat?.color ?? '#666',
          user_name: user?.name ?? 'Unknown',
          user_color: user?.color ?? '#666',
        };
      }).filter(l => {
        const createdAt = new Date(l.created_at);
        return createdAt.getFullYear() <= maxYear;
      });

      setTransactions([...dailyRows, ...recurringRows, ...largeRows]);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYears]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const toggleVerified = async (id: string, currentVerified: boolean) => {
    setTogglingIds(prev => new Set(prev).add(id));

    setTransactions(prev =>
      prev.map(t => (t.id === id ? { ...t, verified: !currentVerified } : t))
    );

    try {
      const { error } = await supabase
        .from('expenses')
        .update({ verified: !currentVerified })
        .eq('id', id);

      if (error) {
        setTransactions(prev =>
          prev.map(t => (t.id === id ? { ...t, verified: currentVerified } : t))
        );
        console.error('Error updating verified status:', error);
      }
    } catch (error) {
      setTransactions(prev =>
        prev.map(t => (t.id === id ? { ...t, verified: currentVerified } : t))
      );
      console.error('Error toggling verified:', error);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const startEditing = (t: TransactionRow) => {
    if (t.type !== 'daily') return;
    setEditingId(t.id);
    setEditAmount(t.amount.toFixed(2));
    setEditNote(t.note ?? '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditAmount('');
    setEditNote('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount <= 0) return;

    const prev = transactions.find(t => t.id === editingId);
    if (!prev) return;

    setTransactions(ts =>
      ts.map(t => t.id === editingId ? { ...t, amount, note: editNote.trim() || undefined } : t)
    );
    setEditingId(null);

    try {
      const { error } = await supabase
        .from('expenses')
        .update({ amount, note: editNote.trim() || null })
        .eq('id', editingId);

      if (error) {
        setTransactions(ts =>
          ts.map(t => t.id === editingId ? { ...t, amount: prev.amount, note: prev.note } : t)
        );
        console.error('Error saving edit:', error);
      }
    } catch (error) {
      setTransactions(ts =>
        ts.map(t => t.id === editingId ? { ...t, amount: prev.amount, note: prev.note } : t)
      );
      console.error('Error saving edit:', error);
    }

    setEditAmount('');
    setEditNote('');
  };

  const deleteTransaction = async (id: string) => {
    if (!window.confirm('Delete this transaction?')) return;

    const prev = transactions.find(t => t.id === id);
    setTransactions(ts => ts.filter(t => t.id !== id));
    setEditingId(null);
    setEditAmount('');
    setEditNote('');

    try {
      const { error } = await supabase.from('expenses').delete().eq('id', id);
      if (error) {
        if (prev) setTransactions(ts => [...ts, prev]);
        console.error('Error deleting transaction:', error);
      }
    } catch (error) {
      if (prev) setTransactions(ts => [...ts, prev]);
      console.error('Error deleting transaction:', error);
    }
  };

  const addTransaction = async () => {
    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount <= 0 || !addCategoryId) return;

    setAddSaving(true);
    try {
      const created_at = new Date(addDate + 'T12:00:00').toISOString();
      const { error } = await supabase
        .from('expenses')
        .insert({
          user_id: addUserId,
          category_id: addCategoryId,
          amount,
          note: addNote.trim() || null,
          created_at,
          verified: false,
        });

      if (error) throw error;

      setShowAddForm(false);
      setAddNote('');
      setAddAmount('');
      setAddDate(new Date().toISOString().slice(0, 10));
      setAddUserId(userId);
      fetchTransactions();
    } catch (error) {
      console.error('Error adding transaction:', error);
    } finally {
      setAddSaving(false);
    }
  };

  const normalCategories = allCategories.filter(c => !c.is_recurring_only && !c.is_large_expense_only);

  const filteredTransactions = transactions.filter(t => {
    if (!showAll && !activeFilters.has(t.type)) return false;

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const amountStr = t.amount.toFixed(2);
    const totalStr = t.totalCharged?.toFixed(2) ?? '';
    return (
      (t.note && t.note.toLowerCase().includes(q)) ||
      t.category_name.toLowerCase().includes(q) ||
      amountStr.includes(q) ||
      totalStr.includes(q) ||
      t.user_name.toLowerCase().includes(q)
    );
  });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFallOff = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return sortNewestFirst ? -diff : diff;
  });

  const dailyFiltered = sortedTransactions.filter(t => t.type === 'daily');
  const verifiedCount = dailyFiltered.filter(t => t.verified).length;
  const totalCount = filteredTransactions.length;

  const filterTags: { id: TxType; label: string; color: string }[] = [
    { id: 'daily', label: 'Daily', color: '#3B82F6' },
    { id: 'recurring', label: 'Recurring', color: '#8B5CF6' },
    { id: 'large', label: 'Large', color: '#F59E0B' },
  ];

  const isTagActive = (id: TxType) => {
    if (allFiltersActive || noFiltersActive) return true;
    return activeFilters.has(id);
  };

  const exportCsv = () => {
    if (sortedTransactions.length === 0) return;

    const escapeCsv = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const headers = ['Type', 'Date', 'Category', 'Note', 'User', 'Amount', 'Monthly Amount', 'Total', 'Verified', 'Falls Off'];
    const rows = sortedTransactions.map(t => {
      const date = t.type === 'daily'
        ? new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';
      const fallOff = t.fallOffDate
        ? new Date(t.fallOffDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : '';

      return [
        t.type.charAt(0).toUpperCase() + t.type.slice(1),
        date,
        escapeCsv(t.category_name),
        escapeCsv(t.note ?? ''),
        escapeCsv(t.user_name),
        t.type === 'daily' ? t.amount.toFixed(2) : '',
        t.monthlyAmount != null ? t.monthlyAmount.toFixed(2) : '',
        t.totalCharged != null ? t.totalCharged.toFixed(2) : '',
        t.type === 'daily' ? (t.verified ? 'Yes' : 'No') : '',
        fallOff,
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const yearsLabel = Array.from(selectedYears).sort().join('-');
    link.href = url;
    link.download = `transactions-${yearsLabel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="transactions-screen">
      <div className="tx-header">
        <h2>Transactions</h2>
        <div className="tx-header-actions">
          <button
            className={`tx-add-btn ${showAddForm ? 'active' : ''}`}
            onClick={() => setShowAddForm(prev => !prev)}
            title="Add transaction"
          >
            <Plus size={20} />
          </button>
          <button className="tx-export-btn" onClick={exportCsv} title="Export CSV">
            <Download size={20} />
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="tx-add-form">
          <div className="tx-add-row">
            <div className="tx-add-field">
              <label>Title / Note</label>
              <input
                type="text"
                value={addNote}
                onChange={e => setAddNote(e.target.value)}
                className="tx-edit-input"
                placeholder="e.g. Groceries at Costco"
                autoFocus
              />
            </div>
            <div className="tx-add-field tx-add-field-amount">
              <label>Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={addAmount}
                onChange={e => setAddAmount(e.target.value)}
                className="tx-edit-input"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="tx-add-row">
            <div className="tx-add-field">
              <label>Date</label>
              <input
                type="date"
                value={addDate}
                onChange={e => setAddDate(e.target.value)}
                className="tx-edit-input"
              />
            </div>
            <div className="tx-add-field">
              <label>User</label>
              <select
                value={addUserId}
                onChange={e => setAddUserId(e.target.value)}
                className="tx-edit-input"
              >
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="tx-add-field">
              <label>Category</label>
              <select
                value={addCategoryId}
                onChange={e => setAddCategoryId(e.target.value)}
                className="tx-edit-input"
              >
                {normalCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="tx-add-actions">
            <button className="tx-edit-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
            <button className="tx-edit-save" onClick={addTransaction} disabled={addSaving}>
              <Check size={14} /> {addSaving ? 'Saving...' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div className="tx-year-selector">
        <button className="tx-year-chevron" onClick={() => setYearRangeStart(y => y - 1)}>
          <ChevronLeft size={18} />
        </button>
        <div className="tx-year-pills">
          {visibleYears.map(y => (
            <button
              key={y}
              className={`tx-year-pill ${selectedYears.has(y) ? 'active' : ''}`}
              onClick={() => toggleYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
        <button className="tx-year-chevron" onClick={() => setYearRangeStart(y => y + 1)}>
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="tx-search-bar">
        <Search size={18} className="tx-search-icon" />
        <input
          type="text"
          className="tx-search-input"
          placeholder="Search by name, category, or amount..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="tx-search-clear" onClick={() => setSearchQuery('')}>
            <X size={16} />
          </button>
        )}
      </div>

      <div className="tx-filter-tags">
        {filterTags.map(tag => {
          const active = isTagActive(tag.id);
          return (
            <button
              key={tag.id}
              className={`tx-filter-tag ${active ? 'active' : ''}`}
              style={{
                borderColor: active ? tag.color : '#444',
                backgroundColor: active ? tag.color : 'transparent',
                color: active ? '#fff' : '#888',
              }}
              onClick={() => toggleFilter(tag.id)}
            >
              {tag.label}
            </button>
          );
        })}
      </div>

      <div className="tx-summary-bar">
        <span className="tx-count">{totalCount} transaction{totalCount !== 1 ? 's' : ''}</span>
        <div className="tx-summary-right">
          <button
            className="tx-sort-btn"
            onClick={() => setSortNewestFirst(prev => !prev)}
            title={sortNewestFirst ? 'Showing newest first' : 'Showing oldest first'}
          >
            <ArrowDownUp size={14} />
            {sortNewestFirst ? 'Newest' : 'Oldest'}
          </button>
          <span className="tx-verified-count">
            <CheckCircle2 size={14} /> {verifiedCount}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : sortedTransactions.length === 0 ? (
        <div className="tx-empty">
          {searchQuery ? 'No transactions match your search.' : 'No transactions found.'}
        </div>
      ) : (
        <div className="tx-list">
          {sortedTransactions.map(t => (
            <div key={t.id} className={`tx-row ${t.verified ? 'tx-verified' : ''} tx-type-${t.type} ${editingId === t.id ? 'tx-editing' : ''}`}>
              {t.type === 'daily' ? (
                <button
                  className={`tx-check-btn ${t.verified ? 'checked' : ''}`}
                  onClick={() => toggleVerified(t.id, t.verified)}
                  disabled={togglingIds.has(t.id)}
                >
                  {t.verified ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                </button>
              ) : (
                <span
                  className="tx-type-indicator"
                  style={{ backgroundColor: t.type === 'recurring' ? '#8B5CF6' : '#F59E0B' }}
                />
              )}

              {editingId === t.id ? (
                <div className="tx-edit-form">
                  <div className="tx-edit-top">
                    <span className="tx-category">
                      <span className="tx-cat-dot" style={{ backgroundColor: t.category_color }} />
                      {t.category_name}
                    </span>
                    <span className="tx-date">{formatDate(t.created_at)}</span>
                  </div>
                  <div className="tx-edit-fields">
                    <div className="tx-edit-field">
                      <label>Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editAmount}
                        onChange={e => setEditAmount(e.target.value)}
                        className="tx-edit-input"
                        autoFocus
                      />
                    </div>
                    <div className="tx-edit-field">
                      <label>Note</label>
                      <input
                        type="text"
                        value={editNote}
                        onChange={e => setEditNote(e.target.value)}
                        className="tx-edit-input"
                        placeholder="Optional note"
                      />
                    </div>
                  </div>
                  <div className="tx-edit-actions">
                    <button className="tx-edit-delete" onClick={() => deleteTransaction(t.id)} title="Delete transaction">
                      <Trash2 size={14} />
                    </button>
                    <div className="tx-edit-actions-right">
                      <button className="tx-edit-cancel" onClick={cancelEditing}>Cancel</button>
                      <button className="tx-edit-save" onClick={saveEdit}>
                        <Check size={14} /> Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="tx-details" onClick={() => t.type === 'daily' && startEditing(t)}>
                  <div className="tx-top-line">
                    <span className="tx-category">
                      <span className="tx-cat-dot" style={{ backgroundColor: t.category_color }} />
                      {t.category_name}
                    </span>
                    {t.type === 'daily' ? (
                      <span className="tx-amount">{formatAmount(t.amount)}</span>
                    ) : (
                      <span className="tx-amount-group">
                        <span className="tx-amount">{formatAmount(t.monthlyAmount!)}/mo</span>
                        <span className="tx-total-charged">{formatAmount(t.totalCharged!)} total</span>
                      </span>
                    )}
                  </div>
                  <div className="tx-bottom-line">
                    {t.type === 'daily' && <span className="tx-date">{formatDate(t.created_at)}</span>}
                    {t.type === 'recurring' && (
                      <span className="tx-type-badge tx-badge-recurring">Recurring</span>
                    )}
                    {t.type === 'large' && (
                      <>
                        <span className="tx-type-badge tx-badge-large">Large</span>
                        {t.fallOffDate && (
                          <span className="tx-fall-off">Falls off {formatFallOff(t.fallOffDate)}</span>
                        )}
                      </>
                    )}
                    {t.note && <span className="tx-note">{t.note}</span>}
                    <span className="tx-user" style={{ color: t.user_color }}>{t.user_name}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionsScreen;
